import { useEffect, useRef } from 'react';
import type { Answers } from '../../domain/entities/answer.entity';

interface QuestionNavigatorProps {
  numbers: number[];
  answers: Answers;
  currentIndex: number;
  onGoTo: (index: number) => void;
}

/**
 * O mapa das questões: um botão por questão, com o estado de cada uma
 * (respondida, em branco, atual) e a contagem. O "Finalizar" não mora aqui: é
 * o botão grande logo abaixo da questão (`QuestionStepper`).
 *
 * **Uma marcação só, dois desenhos**, decididos no CSS (`.qnav`): no celular
 * é a faixa do topo, com rolagem horizontal; no desktop é o card à direita da
 * questão, com os números em grade. Dois componentes, um por layout, cedo ou
 * tarde mostrariam estados diferentes para a mesma questão.
 *
 * "Respondida" é só o que foi **confirmado**: escolher uma alternativa sem
 * confirmar não muda o mapa, porque não muda o que vai no envio.
 */
export function QuestionNavigator({
  numbers,
  answers,
  currentIndex,
  onGoTo,
}: QuestionNavigatorProps) {
  const answeredCount = Object.keys(answers).length;
  const blank = numbers.length - answeredCount;
  const currentRef = useRef<HTMLButtonElement>(null);

  // Na faixa do celular, a questão atual pode estar fora da área visível:
  // traz para o meio sempre que ela muda. No desktop a grade inteira cabe, e
  // o `nearest` não move nada.
  useEffect(() => {
    currentRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'center',
    });
  }, [currentIndex]);

  return (
    <nav className="qnav" aria-label="Navegação das questões">
      <div className="qnav-head">
        <p className="qnav-count">
          {answeredCount} de {numbers.length} respondidas
        </p>
        {answeredCount > 0 && blank > 0 && (
          <p className="qnav-hint">
            {blank === 1
              ? '1 em branco conta como erro.'
              : `${blank} em branco contam como erro.`}
          </p>
        )}
      </div>

      <ol className="qnav-list">
        {numbers.map((number, index) => {
          const isCurrent = index === currentIndex;
          const isAnswered = answers[number] !== undefined;

          return (
            <li key={number}>
              <button
                ref={isCurrent ? currentRef : undefined}
                type="button"
                className={`qnav-item ${isAnswered ? 'is-answered' : ''} ${isCurrent ? 'is-current' : ''}`}
                onClick={() => onGoTo(index)}
                aria-current={isCurrent ? 'step' : undefined}
                aria-label={`Questão ${number}, ${isAnswered ? 'respondida' : 'em branco'}`}
              >
                {number}
              </button>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
